import React, { useEffect, useMemo, useRef, useState } from "react";
import "./thankyou.css";
import axios from "axios";
import { useNavigate } from 'react-router-dom';
import Swal from "sweetalert2";
import { updategameover, getScore, updateFeedback } from "../../scoreservice";

function Thankyou({ updateHeader }) {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [themeData, setThemeData] = useState(null);
  const storedUserData = JSON.parse(sessionStorage.getItem('userData'));

  const [textcolor, Setcolorcode] = useState(null);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(null);
  const [timePlayedFromDb, setTimePlayedFromDb] = useState("");
  const [playedTime, setPlayedTime] = useState("00:00");
  const [playedTimeReady, setPlayedTimeReady] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);
  const [feedbackValue, setFeedbackValue] = useState(null);
  const [feedbackToastKey, setFeedbackToastKey] = useState(0);
  const [glitterActive, setGlitterActive] = useState(false);

  const S3_URL = (process.env.REACT_APP_S3_PATH || "").replace(/\/*$/, "/");

  // ── Drag state ──────────────────────────────────────────────────────────────
  const stageRef      = useRef(null);   // .ty-feedback-area (coordinate space)
  const dropRef       = useRef(null);   // dreamcatcher drop target
  const cloudRefs     = useRef({});
  const gameoverSentRef = useRef(false);
  const dragFlipLockRef = useRef({}); // { [id]: "-1"|"1" } while dragging

  const [draggingId, setDraggingId]   = useState(null);
  // pixelPos: only set while a cloud is being dragged; null = use CSS position
  const [pixelPos, setPixelPos]       = useState({});
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const rafRef        = useRef(null);
  const latestPosRef  = useRef(null);

  // ── Feedback options ────────────────────────────────────────────────────────
  const feedbackOptions = useMemo(() => [
    { id: "good",    label: "Good"    },
    { id: "loved",   label: "Loved"   },
    { id: "amazing", label: "Amazing" },
    { id: "liked",   label: "Liked"   },
  ], []);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const applyCloudFlip = () => {
    const stageEl = stageRef.current;
    if (!stageEl) return;
    const stageRect  = stageEl.getBoundingClientRect();
    const stageMidX  = stageRect.left + stageRect.width / 2;
    Object.keys(cloudRefs.current).forEach((id) => {
      const cloudEl = cloudRefs.current[id];
      const imgEl   = cloudEl?.querySelector(".ty-cloud-img");
      if (!cloudEl || !imgEl) return;

      // If this cloud is being dragged, keep its flip locked (no flip while dragging)
      if (dragFlipLockRef.current?.[id]) {
        imgEl.style.setProperty("--cloud-flip", dragFlipLockRef.current[id]);
        return;
      }

      const rect    = cloudEl.getBoundingClientRect();
      const midX    = rect.left + rect.width / 2;
      imgEl.style.setProperty("--cloud-flip", midX < stageMidX ? "-1" : "1");
    });
  };

  const submitFeedback = async (value) => {
    if (!storedUserData?.sessionId) return;
    try {
      await updateFeedback({
        userId:         storedUserData.userId || storedUserData.userid,
        sessionId:      storedUserData.sessionId,
        organizationId: storedUserData.organizationId,
        feedback:       value,
      });
    } catch (e) { /* swallow */ }
  };

  // ── Pointer handlers ────────────────────────────────────────────────────────
  const onCloudPointerDown = (e, id) => {
    if (feedbackDone) return;

    const cloudEl = cloudRefs.current[id];
    if (!cloudEl) return;

    // Capture current rendered center in feedback-area coords
    const areaRect  = stageRef.current.getBoundingClientRect();
    const cloudRect = cloudEl.getBoundingClientRect();

    // Offset from pointer to cloud top-left (in area coords)
    dragOffsetRef.current = {
      x: e.clientX - cloudRect.left,
      y: e.clientY - cloudRect.top,
    };

    // Seed pixelPos with current visual position so cloud doesn't jump
    const initLeft = cloudRect.left - areaRect.left;
    const initTop  = cloudRect.top  - areaRect.top;

    setPixelPos(prev => ({
      ...prev,
      [id]: { left: initLeft, top: initTop },
    }));
    setDraggingId(id);

    // Lock current flip so it doesn't flip while dragging
    const imgEl = cloudEl.querySelector(".ty-cloud-img");
    const lockedFlip =
      (imgEl && imgEl.style.getPropertyValue("--cloud-flip")) ||
      "-1"; // default is fine; will be corrected on next applyCloudFlip call
    dragFlipLockRef.current[id] = String(lockedFlip).trim() || "-1";

    cloudEl.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  };

  const onAreaPointerMove = (e) => {
    if (!draggingId || feedbackDone) return;

    const areaRect = stageRef.current?.getBoundingClientRect?.();
    if (!areaRect) return;

    const x = e.clientX - areaRect.left - dragOffsetRef.current.x;
    const y = e.clientY - areaRect.top  - dragOffsetRef.current.y;

    latestPosRef.current = { id: draggingId, x, y };

    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      const mv = latestPosRef.current;
      if (!mv) return;
      setPixelPos(prev => ({
        ...prev,
        [mv.id]: { left: mv.x, top: mv.y },
      }));
    });

    e.preventDefault();
  };

  const onAreaPointerUp = async (e) => {
    if (!draggingId || feedbackDone) {
      setDraggingId(null);
      return;
    }

    // Hit-test: is cloud overlapping dreamcatcher?
    const cloudEl  = cloudRefs.current[draggingId];
    const dropRect = dropRef.current?.getBoundingClientRect?.();
    const cloudRect= cloudEl?.getBoundingClientRect?.();

    const inside =
      dropRect && cloudRect &&
      cloudRect.left   < dropRect.right  &&
      cloudRect.right  > dropRect.left   &&
      cloudRect.top    < dropRect.bottom &&
      cloudRect.bottom > dropRect.top;

    if (!inside) {
      // Snap back to CSS position
      setPixelPos(prev => {
        const next = { ...prev };
        delete next[draggingId];
        return next;
      });
      setDraggingId(null);
      return;
    }

    // ── Dropped on target ──
    const opt = feedbackOptions.find(o => o.id === draggingId);
    const val = opt?.label || draggingId;
    setFeedbackValue(val);
    setFeedbackDone(true);
    setFeedbackToastKey(k => k + 1);
    setDraggingId(null);
    // Unlock flips after drop (we hide clouds anyway)
    dragFlipLockRef.current = {};
    await submitFeedback(val);

    setGlitterActive(true);
    setTimeout(() => {
      // Replace so browser-back won't return to Thankyou
      navigate("/leaderboard", { replace: true });
    }, 900);
  };

  // ── Cloud flip on layout / resize ───────────────────────────────────────────
  useEffect(() => {
    const raf = requestAnimationFrame(applyCloudFlip);
    return () => cancelAnimationFrame(raf);
  }, [pixelPos, draggingId, themeData]);

  useEffect(() => {
    window.addEventListener("resize", applyCloudFlip);
    return () => window.removeEventListener("resize", applyCloudFlip);
  }, []);

  // ── Session / theme setup ───────────────────────────────────────────────────
  useEffect(() => {
    updateHeader({ backButtonRedirect: "/game" });

    const storedSession = sessionStorage.getItem("userData");
    const storedTheme   = sessionStorage.getItem("themeData");
    if (storedSession && storedTheme) {
      const parsedUser  = JSON.parse(storedSession);
      const parsedTheme = JSON.parse(storedTheme);
      setUserData(parsedUser);
      setThemeData(parsedTheme);
      Setcolorcode(parsedTheme.icon_color || parsedTheme.landing_page_title_color);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // ── Fetch score ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!storedUserData?.sessionId) return;
    const fetchScore = async () => {
      const res = await getScore({
        userId:         storedUserData.userId || storedUserData.userid,
        sessionId:      storedUserData.sessionId,
        organizationId: storedUserData.organizationId,
      });
      if (res.status === "success") {
        const fb = String(res?.data?.feedback || "").trim();
        if (fb) {
          // Feedback already submitted → go straight to leaderboard
          navigate("/leaderboard", { replace: true });
          return;
        }
        setScore(res.data.total_score);
        setTime(res.data.time);
        setTimePlayedFromDb(String(res?.data?.time_played || "").trim());
      }
    };
    fetchScore();
  }, []);

  // ── Compute playedTime ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!themeData || time == null) return;

    // If backend already stored played time (especially for timeout==true), use it.
    if (String(timePlayedFromDb || "").trim()) {
      const formatted = String(timePlayedFromDb).trim();
      setPlayedTime(formatted);
      sessionStorage.setItem("playedTime", formatted);
      setPlayedTimeReady(true);
      return;
    }

    const toSeconds = (val) => {
      if (!val) return 0;
      const s = String(val);
      if (s.includes(":")) {
        const parts = s.split(":").map(Number);
        if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
        return parts[0] * 60 + parts[1];
      }
      return Number(s);
    };

    if (themeData.use_timeout === "1") {
      const remaining = toSeconds(String(time));
      const total     = toSeconds(String(themeData.timer));
      const played    = remaining <= 1 ? total : total - remaining;
      const m = Math.floor(played / 60);
      const s = played % 60;
      const formatted = `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
      setPlayedTime(formatted);
      sessionStorage.setItem("playedTime", formatted);
      setPlayedTimeReady(true);
    } else {
      setPlayedTime(time);
      sessionStorage.setItem("playedTime", String(time));
      setPlayedTimeReady(true);
    }
  }, [themeData, time, timePlayedFromDb]);

  // ── Send gameover + report update ───────────────────────────────────────────
  useEffect(() => {
    if (!storedUserData?.sessionId) return;
    if (!themeData)  return;
    if (time == null) return;
    if (!playedTimeReady) return;
    if (gameoverSentRef.current) return;

    // Ensure we send the right time to report/gameover
    // - timeout enabled: prefer DB time_played (if present) else computed playedTime
    // - timeout disabled: playedTime is already elapsed time
    const safePlayedTime =
      themeData?.use_timeout === "1"
        ? (String(timePlayedFromDb || "").trim() || playedTime || "00:00")
        : (playedTime || "00:00");
    gameoverSentRef.current = true;

    const sendReportUpdate = async () => {
      try {
        const isGuest  = [storedUserData?.roles, storedUserData?.source, storedUserData?.user_type, storedUserData?.role].includes("GUEST_USER");
        const ReportId = storedUserData?.reportId || storedUserData?.report_id;

        const reportUrl = isGuest
          ? `${process.env.REACT_APP_REPORTS_BASE_URL}/game/update/guest/score`
          : `${process.env.REACT_APP_REPORTS_BASE_URL}/game-server/report/${ReportId}`;

        const headers = {
          "client-secret":  process.env.REACT_APP_CLIENT_SECRET,
          "Content-Type":   "application/json",
          "Idempotency-Key": crypto.randomUUID(),
        };
        if (!isGuest) headers["Authorization"] = `Bearer ${storedUserData.token}`;

        if (!isGuest && !ReportId) {
          console.warn("No reportId stored for this player: report update skipped");
          return;
        }
        const res = await axios.put(reportUrl, { reportId: ReportId, points: Number(score), time: safePlayedTime }, { headers });
        console.log("Report update response:", res.data);
      } catch (error) {
        console.error("Report update failed:", error);
      }
    };

    updategameover({
      sessionId:      storedUserData.sessionId,
      organizationId: storedUserData.organizationId,
      userId:         storedUserData.userId || storedUserData.userid,
      gameover:       true,
      time_played:    safePlayedTime,
    });
    sendReportUpdate();
  }, [themeData, time, playedTime, score, playedTimeReady, timePlayedFromDb]);

  // ── Dynamic background ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!themeData) return;
    const body = document.body;
    const update = () => {
      if (!body.classList.contains("common-bg")) return;
      const isMobile   = window.innerWidth <= 768;
      const isPortrait = window.matchMedia("(orientation: portrait)").matches;
      body.style.backgroundImage = `url(${
        isMobile && isPortrait ? themeData.background_mob : themeData.background_desk
      })`;
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [themeData]);

  // (Removed) Zydus-specific redirect logic

  // ── Build per-cloud style ───────────────────────────────────────────────────
  const cloudStyle = (id) => {
    const px = pixelPos[id];
    // While dragging OR if a pixel snapshot exists, use pixel coords
    if (px) {
      return { left: px.left, top: px.top, transform: "translate(0, 0)" };
    }
    // Otherwise fall back to CSS classes (defined in .ty-cloud[data-fb-id="..."])
    return {};
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div
      className="ty-stage"
      style={{
        "--ty-accent": themeData?.icon_color || themeData?.landing_page_title_color || "#ffffff",
        backgroundImage: themeData?.all_icon_image
          ? `url(${S3_URL}${themeData.all_icon_image})`
          : undefined,
      }}
    >
      {glitterActive ? (
        <div className="ty-glitter" aria-hidden="true">
          <div className="ty-glitter-layer ty-glitter-layer1" />
          <div className="ty-glitter-layer ty-glitter-layer2" />
          <div className="ty-glitter-layer ty-glitter-layer3" />
        </div>
      ) : null}
      <div className="ty-overlay">
        <div className="ty-card">
          <div className="ty-title">How was this dreamy experience?</div>

          {/* feedback-area is the coordinate space for cloud positions */}
          <div
            ref={stageRef}
            className="ty-feedback-area"
            onPointerMove={onAreaPointerMove}
            onPointerUp={onAreaPointerUp}
            onPointerCancel={onAreaPointerUp}
          >
            {/* Drop target */}
            <div ref={dropRef} className="ty-drop" aria-hidden="true">
              <img
                className="ty-drop-img"
                src={
                  themeData?.dreamcatchter_image
                    ? `${S3_URL}${themeData.dreamcatchter_image}`
                    : ""
                }
                alt="dreamcatcher"
                draggable={false}
              />
            </div>

            {/* Cloud options */}
            {feedbackOptions.map((opt) => (
              <div
                key={opt.id}
                data-fb-id={opt.id}
                className={[
                  "ty-cloud",
                  draggingId === opt.id ? "is-dragging" : "",
                  feedbackDone          ? "is-disabled"  : "",
                ].filter(Boolean).join(" ")}
                ref={(el) => { if (el) cloudRefs.current[opt.id] = el; }}
                style={cloudStyle(opt.id)}
                onPointerDown={(e) => onCloudPointerDown(e, opt.id)}
                role="button"
                aria-label={`Rate as ${opt.label}`}
              >
                <img
                  className="ty-cloud-img"
                  src={themeData?.cloud_image ? `${S3_URL}${themeData.cloud_image}` : ""}
                  alt=""
                  draggable={false}
                />
                <div className="ty-cloud-text">{opt.label}</div>
              </div>
            ))}
          </div>

          {feedbackDone ? (
            <div key={feedbackToastKey} className="ty-feedback-thanks">
              Thank you for your feedback ✨
            </div>
          ) : (
            <div className="ty-feedback-hint">
              Drag a cloud and drop it on the dreamcatcher.
            </div>
          )}
        </div>

        <div className="ty-bottom">
          <div className="main_text_ty" style={{ color: textcolor }}>
            <strong>{themeData?.custom_text_thank_you_page}</strong>
          </div>
          <p className="score" style={{ margin: "8px 0 0", color: textcolor }}>
            You have scored <strong>{score}</strong> in{" "}
            <strong>{playedTime}</strong> minutes.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Thankyou;
